import { h } from '@omni-torn/ui';
import { ACCOUNT_LEVELS, KEY_LEVEL_NAMES, OVERVIEW_CARDS, WEB_SECTIONS, type KeyLevel } from '@omni-torn/shared';
import { extension } from '../bridge';
import { markTrackingSeen, signInWithCode, signInWithExtension, signInWithKey } from '../session';
import { box, keyMakeLink } from '../ui';
import { termsTable } from './keys';

const CARD_NAMES: Record<keyof typeof OVERVIEW_CARDS, string> = { status: 'status', bars: 'bars', cooldowns: 'cooldowns', travel: 'flight', refills: 'refills', money: 'money', networth: 'networth' };

/** What each key level adds (from the section list, so it never goes out of date). */
function levelTable(): HTMLElement {
  const rows = ([1, 2, 3, 4] as KeyLevel[]).map((lvl) => {
    const sections = WEB_SECTIONS.filter((s) => s.key.level === lvl && !s.access && !s.always && s.group !== 'torn').map((s) => s.name);
    // Torn's own lists and calculators: one entry (any key opens them).
    if (WEB_SECTIONS.some((s) => s.key.level === lvl && s.group === 'torn')) sections.push('Torn’s lists (items, bank, Hall of Fame, factions…) and calculators');
    const cards = (Object.keys(OVERVIEW_CARDS) as Array<keyof typeof OVERVIEW_CARDS>).filter((c) => OVERVIEW_CARDS[c].level === lvl && c !== 'status').map((c) => CARD_NAMES[c]);
    const adds = [...sections, ...(cards.length ? [`overview: ${cards.join(', ')}`] : [])];
    return h('tr', { class: lvl === 3 ? 'recommended' : '' }, h('th', { scope: 'row' }, KEY_LEVEL_NAMES[lvl], lvl === 3 ? h('span', { class: 'tag' }, 'recommended') : null), h('td', {}, lvl === 1 ? adds.join(' · ') : `+ ${adds.join(' · ')}`), h('td', {}, keyMakeLink(lvl, 'Make one ↗')));
  });
  return h('div', { class: 'table-wrap' }, h('table', { class: 'levels-table' }, h('thead', {}, h('tr', {}, h('th', {}, 'Key'), h('th', {}, 'What it opens'), h('th', {}, ''))), h('tbody', {}, ...rows)));
}

export async function renderSignIn(view: HTMLElement, onDone: () => void, note = ''): Promise<void> {
  document.title = 'Sign in · Omni-Torn';
  const input = h('input', { id: 'api-key', type: 'password', autocomplete: 'off', spellcheck: 'false', maxlength: '16', placeholder: '16-character API key', 'aria-describedby': 'key-help' }) as HTMLInputElement;
  const remember = h('input', { type: 'checkbox', id: 'remember' }) as HTMLInputElement;
  const status = h('p', { class: 'form-status', role: 'status' }, note);
  if (note) status.classList.add('bad');
  // One agreement, both uses (terms 17): the table is above; this button agrees to it and gives the key for both.
  const go = h('button', { type: 'submit', class: 'btn-primary' }, 'Agree and use my key');
  const form = h(
    'form',
    { class: 'signin-form', autocomplete: 'off' },
    h('label', { for: 'api-key' }, 'Your Torn API key'),
    h('div', { class: 'key-row' }, input, go),
    h('label', { class: 'check' }, remember, ' Remember me on this device'),
    h('p', { id: 'key-help', class: 'muted' }, 'Torn → Settings → API Keys. A Limited key is recommended (Torn asks tools to ask for no more than they need); Full adds only your activity log (Logs). A lower key works too — the sections it can’t read are locked and say which key they need.'),
    status,
  );
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    go.disabled = true;
    status.className = 'form-status';
    status.textContent = 'Checking your key with Torn…';
    try {
      await signInWithKey(input.value, remember.checked);
      input.value = '';
      onDone();
    } catch (err) {
      status.className = 'form-status bad';
      status.textContent = (err as Error).message;
      go.disabled = false;
    }
  });

  // Signed in somewhere else already (Discord /account connect, the extension, another browser): a code.
  const codeInput = h('input', { id: 'connect-code', type: 'text', autocomplete: 'off', spellcheck: 'false', maxlength: '9', placeholder: 'K7QM-3XRA', 'aria-label': 'Code' }) as HTMLInputElement;
  const codeRemember = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const codeStatus = h('p', { class: 'form-status', role: 'status' });
  const codeGo = h('button', { type: 'submit', class: 'btn-primary' }, 'Sign in with the code');
  const codeForm = h('form', { class: 'signin-form code-form', autocomplete: 'off' }, h('div', { class: 'key-row' }, codeInput, codeGo), h('label', { class: 'check' }, codeRemember, ' Remember me on this device'), codeStatus);
  codeForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    codeGo.disabled = true;
    codeStatus.className = 'form-status';
    codeStatus.textContent = 'Asking the Omni-Torn server…';
    try {
      await signInWithCode(codeInput.value, codeRemember.checked);
      codeInput.value = '';
      onDone();
    } catch (err) {
      codeStatus.className = 'form-status bad';
      codeStatus.textContent = (err as Error).message;
      codeGo.disabled = false;
    }
  });
  const extBox = h('div', { class: 'ext-signin', hidden: true });
  view.replaceChildren(
    h(
      'div',
      { class: 'signin' },
      h(
        'section',
        { class: 'hero' },
        h('h1', {}, 'Your Torn, in one place'),
        h('p', { class: 'lead' }, 'Bars, cooldowns, flights, stats and their history, fights, logs, your faction’s shared cooldowns — read with your own key, shown only to you.'),
      ),
      extBox,
      // Torn's terms: the table before the key is given.
      box('How Omni-Torn uses your key', termsTable(), { id: 'signin-terms' }),
      box('Sign in with your API key', form, { cls: 'signin-box' }),
      box('Have a code?', [h('p', { class: 'muted' }, 'Gave Omni-Torn your key in Discord (/account connect), the extension or another browser? A code from there signs you in here with the same key and settings — no typing the key. Codes work once, for 10 minutes.'), codeForm], { cls: 'signin-box code-box' }),
      box('Which key?', [levelTable(), h('p', { class: 'muted' }, 'Keys only read. Omni-Torn can never do anything in Torn with them.'), h('h3', {}, 'What Omni-Torn reads for your account, by key level'), h('ul', { class: 'plain-list' }, ...ACCOUNT_LEVELS.map((l) => h('li', {}, h('strong', {}, `${l.name}: `), l.reads)))]),
      box('In short', h('ul', { class: 'plain-list' }, h('li', {}, 'It stays in this browser, encrypted. Torn’s API is asked straight from here.'), h('li', {}, 'The Omni-Torn server keeps the key you sign in with — encrypted — to keep your own data up to date in the background (history, fights, log, your faction share). To stop, remove your key everywhere or delete your data (website → Account) and the server deletes it at once.'),
          h('li', {}, 'One key for this site, the extension and the Discord bot, one agreement: it serves your account (background tracking, signing in elsewhere) and your faction (sharing with faction mates, the Discord bot’s faction reads) — in no faction that part just waits. Remove it everywhere, or delete your data, any time in Account.'), h('li', {}, 'What you see is only shown to you. Sharing with your faction (bars, cooldowns, flights, status, battle stats total, loadout…) is part of using Omni-Torn, with no switch — the table above says what goes where (every detail: the full terms).'), h('li', {}, 'Signing out here forgets it in this browser. Deleting the key on Torn (Settings → API Keys) stops every use of it, ours included.'), h('li', {}, h('a', { href: '/privacy.html' }, 'Privacy policy')))),
    ),
  );
  input.focus();

  // The extension, signed in here already: one click instead of a key.
  const ext = await extension();
  if (ext?.signedIn) {
    const btn = h('button', { type: 'button', class: 'btn-primary' }, `Sign in with the extension${ext.name ? ` as ${ext.name}` : ''}`);
    const rem = h('input', { type: 'checkbox' }) as HTMLInputElement;
    const st = h('p', { class: 'form-status', role: 'status' });
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      st.textContent = 'Asking the extension…';
      try {
        markTrackingSeen();
        await signInWithExtension(rem.checked);
        onDone();
      } catch (err) {
        st.className = 'form-status bad';
        st.textContent = (err as Error).message;
        btn.disabled = false;
      }
    });
    extBox.replaceChildren(box('The Omni-Torn extension is here', [h('p', {}, 'It’s signed in already — use it, and your key stays in the extension.'), btn, h('label', { class: 'check' }, rem, ' Remember me on this device'), st], { cls: 'signin-box ext-box' }));
    extBox.hidden = false;
  } else if (ext) {
    extBox.replaceChildren(box('The Omni-Torn extension is here', h('p', { class: 'muted' }, 'Sign it in first (its options page), then you can sign in here with one click.')));
    extBox.hidden = false;
  }
}
